/**
 * 題目頁停著時查詢語音狀態的節奏與通知。
 *
 * 存檔後語音是背景產生的（studio-api `QuestAudioService`，每句約 3–9 秒），
 * 題目詳情的 `media` 只是載入當下的快照。有句子「產生中」時，`UnitQuestEdit` 用
 * `GET /diary/:id/quests/:questId/audio` 定時查，每次查完問這裡下一步怎麼做。
 *
 * 驗證：`node docs/plans/VideoQuest.verify-audio-polling.cjs`
 */

/// 間隔：5 → 10 → 20 → 30 秒封頂。第一次查詢也等這麼久（存檔當下才剛排進去）
export const FIRST_DELAY_MS = 5000;
const MAX_DELAY_MS = 30000;
/// 查這麼久還在產生中，多半是工作卡住了（cron 會回收）。停止查詢、不通知，避免頁面一直打 API
const TIME_LIMIT_MS = 10 * 60 * 1000;

const BUSY_STATUSES = ['pending', 'processing'];

/**
 * @param {Object<string, string>} statuses 這一題每筆語音的 `process_status`
 * @param {number} elapsedMs 從開始查詢到現在的時間
 * @param {number} attempt 已經查了幾次（0 起）
 * @returns {{done: boolean, delayMs: number,
 *   notice: null | {type: 'ready'|'failed', count: number}}}
 *   `done` 時 `notice` 是結束時要發的通知：全部完成發 `ready`，有失敗的發 `failed`
 *   並帶句數（重試的方式是再存一次檔，見 `UnitQuestEdit.onAudioRetry`）。
 *   查超過 10 分鐘還有產生中的、或一筆都查不到的，也是 `done`，但 `notice` 是 null。
 */
export function audioPollStep(statuses, elapsedMs, attempt) {
    const values = Object.values(statuses || {});
    if (values.length === 0) {
        /// 一筆都查不到：沒有東西可以宣告「完成」
        return { done: true, delayMs: 0, notice: null };
    }
    const busy = values.filter((status) => BUSY_STATUSES.includes(status)).length;
    if (busy > 0 && elapsedMs >= TIME_LIMIT_MS) {
        return { done: true, delayMs: 0, notice: null };
    }
    if (busy > 0) {
        return {
            done: false,
            delayMs: Math.min(FIRST_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS),
            notice: null,
        };
    }
    const failed = values.filter((status) => status === 'failed').length;
    return {
        done: true,
        delayMs: 0,
        notice: failed > 0 ? { type: 'failed', count: failed } : { type: 'ready', count: 0 },
    };
}
